<script setup lang="ts">
/**
 * Sidebar droite de la page événement :
 *  - card Location (map + adresse + contact)
 *  - card Order summary (visible step >=2)
 *  - card branding Donoor (step 1)
 */
import { lienTelephone, telephoneAffiche, texteItineraireLieu } from "@donoor/utils";
import type { Event, TicketType, TicketOption } from "~/types/event";
import { fraisDesParts, type PartDuPanier } from "~/utils/plusieursFois";
import PublicPageTotauxPlusieursFois from "~/components/public-page/PublicPageTotauxPlusieursFois.vue";

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

interface AttendeeLocal {
  firstName: string;
  lastName: string;
  selectedOptions: Record<string, number>;
}

const props = defineProps<{
  event: Event;
  checkoutStep: number;
  totalSelected: number;
  ticketTypes: TicketType[];
  selections: Record<string, number>;
  attendees: AttendeeLocal[];
  ticketOptions: TicketOption[];
  ticketsOnlyPrice: number;
  optionsTotal: number;
  promoApplied: { code: string; discount_type: string; discount_value: number } | null;
  promoCode: string;
  promoLoading: boolean;
  promoError: string;
  promoDiscount: number;
  donationAmount: number;
  totalPrice: number;
  feesAmount: number;
  includeFees: boolean;
  donateUs: boolean;
  donateToUsAmount: number;
  /** Parts du paiement en plusieurs fois choisi (vide : en une fois). */
  partsPlusieursFois?: PartDuPanier[];
}>();

// En plusieurs fois : frais de TOUTE la commande (un frais fixe par
// paiement) et bas du détail = total réel, aujourd'hui, puis la suite.
const enPlusieursFois = computed(() => (props.partsPlusieursFois?.length ?? 0) >= 2);
const fraisAffiches = computed(() =>
  enPlusieursFois.value
    ? fraisDesParts(props.partsPlusieursFois ?? [], props.totalPrice + props.donationAmount + (props.donateUs ? props.donateToUsAmount : 0))
    : props.feesAmount,
);

defineEmits<{
  (e: 'update:promo-code', v: string): void;
  (e: 'apply-promo' | 'remove-promo'): void;
}>();

// Lignes d'adresse dédupliquées (name/address/city se chevauchent souvent :
// address contient déjà la ville → city ne doit pas réapparaître). Parité
// stricte avec AslugSidebar (page adhésion). Karim 2026-07-08.
const addressLines = computed(() => buildAddressLines(props.event));

// Carte (lot 4) : point ENREGISTRÉ du lieu s'il existe (aucun géocodage) ;
// sinon repli sur le texte du lieu, règle partagée `texteItineraireLieu`
// (adresse + ville sans doublon, sinon le nom du lieu) : MÊME texte
// d'itinéraire que la page adhésion, le dashboard et le mobile. Remplace la
// concaténation locale de la page (« adresse, ville » même quand l'adresse
// contenait déjà la ville).
const texteCarte = computed(() =>
  texteItineraireLieu(props.event.location_address, props.event.location_city, props.event.location_name),
);
</script>

<template>
  <div class="lg:w-[320px] shrink-0 mt-0 md:mt-4 lg:mt-6 pb-6">
    <div class="lg:sticky lg:top-6 space-y-4 md:space-y-6">

      <!-- Location Card -->
      <div v-if="event.location_name || event.location_address" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
        <ClientOnly>
          <MiniMap
            :address="texteCarte"
            :latitude="event.location_latitude"
            :longitude="event.location_longitude"
            :precision="event.location_precision"
            :pays="event.location_country || event.country"
            :territoire="event.location_territory"
            :departement="event.location_department"
            :ville-lieu="event.location_city"
            height="180px"
          />
          <template #fallback>
            <div class="h-[180px] bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              <UIcon name="i-lucide-map" class="size-10 text-gray-400 dark:text-gray-600" />
            </div>
          </template>
        </ClientOnly>
        <div class="p-5 space-y-1">
          <p
            v-for="(line, i) in addressLines"
            :key="i"
            class="text-sm"
            :class="i === 0 ? 'font-semibold text-[#2d2e5c] dark:text-white' : 'text-[#505870] dark:text-gray-400'"
          >
            {{ line }}
          </p>
        </div>
        <div v-if="event.contact_email || event.contact_phone" class="border-t border-gray-100 dark:border-gray-800 p-5">
          <p class="text-xs text-[#717198] dark:text-gray-500 mb-1">{{ t('billetterie.bslugSidebar.uneQuestion') }}</p>
          <p class="text-xs text-[#717198] dark:text-gray-500">{{ t('billetterie.bslugSidebar.contactezOrganisateur') }}</p>
          <a v-if="event.contact_email" :href="`mailto:${event.contact_email}`" class="block mt-2 text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ event.contact_email }}</a>
          <a v-if="event.contact_phone" :href="lienTelephone(event.contact_phone, event.country)" class="block mt-1 text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ telephoneAffiche(event.contact_phone, event.country) }}</a>
        </div>
      </div>

      <!-- Order summary — masqué sur mobile (< lg) : le récap complet (avec
           code promo + breakdown) vit déjà dans le corps au step récap, on
           évite le doublon. Reste visible en sidebar sticky sur desktop. -->
      <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 translate-y-2" leave-active-class="transition-all duration-200 ease-in" leave-to-class="opacity-0 -translate-y-2">
        <div v-if="checkoutStep >= 2 && totalSelected > 0" class="hidden lg:block bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
          <div class="p-5">
            <h3 class="text-base font-bold text-[#2d2e5c] dark:text-white mb-4">{{ t('billetterie.bslugSidebar.detailsCommande') }}</h3>

            <div class="space-y-3">
              <div v-for="tt in ticketTypes.filter((t: TicketType) => (selections[t.id] || 0) > 0)" :key="tt.id" class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-[#2d2e5c] dark:text-white">{{ tt.name }} <span class="text-[#717198]">x{{ selections[tt.id] || 0 }}</span></p>
                </div>
                <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white shrink-0 ml-3">
                  {{ Number(tt.price) > 0 ? formatCurrency(Number(tt.price) * (selections[tt.id] || 0)) : t('billetterie.partage.gratuit') }}
                </span>
              </div>

              <template v-if="checkoutStep >= 2 && attendees.some(a => Object.values(a.selectedOptions).some(q => q > 0))">
                <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
                  <div v-for="(a, aIdx) in attendees.filter(a => Object.values(a.selectedOptions).some(q => q > 0))" :key="'opts-' + aIdx" class="mb-2 last:mb-0">
                    <p class="text-[11px] text-[#717198] dark:text-gray-500 mb-1">{{ a.firstName || t('billetterie.bslugSidebar.participantParDefaut') }} {{ a.firstName ? '' : aIdx + 1 }}</p>
                    <template v-for="(qty, optId) in a.selectedOptions" :key="optId">
                      <div v-if="qty > 0" class="flex items-center justify-between ml-2">
                        <p class="text-xs text-[#717198] dark:text-gray-400">
                          <UIcon name="i-lucide-plus-circle" class="size-3 inline -mt-0.5 mr-0.5" />
                          {{ ticketOptions.find((o: TicketOption) => o.id === optId)?.name }}{{ qty > 1 ? ` ×${qty}` : '' }}
                        </p>
                        <span class="text-xs text-[#717198] dark:text-gray-400 shrink-0 ml-3">
                          {{ Number(ticketOptions.find((o: TicketOption) => o.id === optId)?.price || 0) > 0 ? '+' + formatCurrency(Number(ticketOptions.find((o: TicketOption) => o.id === optId)?.price) * qty) : t('billetterie.partage.gratuit') }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </template>
            </div>

            <!-- Promo input + breakdown (frais + soutien Donoor) UNIQUEMENT
                 au step récap (4). Avant : juste les billets+options pour une
                 progression UX claire (Karim 2026-06-21). -->
            <div v-if="checkoutStep >= 4 && (ticketsOnlyPrice + optionsTotal) > 0" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <PublicPagePromoCodeInput
                variant="b"
                :model-value="promoCode"
                :applied="promoApplied"
                :loading="promoLoading"
                :error="promoError"
                @update:model-value="$emit('update:promo-code', $event)"
                @apply="$emit('apply-promo')"
                @remove="$emit('remove-promo')"
              />
            </div>

            <div v-if="checkoutStep >= 4 && promoDiscount > 0" class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <span class="text-sm text-emerald-600">{{ t('billetterie.bslugSidebar.reduction') }}</span>
              <span class="text-sm font-semibold text-emerald-600">-{{ formatCurrency(promoDiscount) }}</span>
            </div>
          </div>

          <div v-if="checkoutStep >= 4 && (donationAmount > 0 || (includeFees && feesAmount > 0) || (donateUs && donateToUsAmount > 0))" class="px-5 pb-2 space-y-1.5">
            <div v-if="donationAmount > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ t('billetterie.bslugSidebar.donA', { association: event.organization_name || t('billetterie.partage.associationParDefaut') }) }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(donationAmount) }}</span>
            </div>
            <div v-if="includeFees && fraisAffiches > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ t('billetterie.partage.fraisTransaction') }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(fraisAffiches) }}</span>
            </div>
            <div v-if="donateUs && donateToUsAmount > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ t('billetterie.partage.soutienDonoor') }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(donateToUsAmount) }}</span>
            </div>
          </div>

          <div v-if="checkoutStep >= 4 && enPlusieursFois" class="border-t border-gray-100 px-5 py-4">
            <PublicPageTotauxPlusieursFois :parts="partsPlusieursFois ?? []" />
          </div>
          <div v-else class="border-t border-gray-100 px-5 py-4">
            <div class="flex items-center justify-between">
              <span class="text-base font-bold text-[#2d2e5c]">{{ checkoutStep >= 4 ? t('billetterie.partage.totalAPayer') : t('billetterie.bslugSidebar.sousTotal') }}</span>
              <span class="text-lg font-bold" :style="{ color: 'var(--accent, #2d2e5c)' }">
                {{ checkoutStep >= 4
                  ? ((totalPrice + donationAmount + (includeFees ? feesAmount : 0) + (donateUs ? donateToUsAmount : 0)) > 0
                    ? formatCurrency(totalPrice + donationAmount + (includeFees ? feesAmount : 0) + (donateUs ? donateToUsAmount : 0))
                    : t('billetterie.partage.gratuit'))
                  : (totalPrice > 0 ? formatCurrency(totalPrice) : t('billetterie.partage.gratuit')) }}
              </span>
            </div>
          </div>
        </div>
      </Transition>

      <PublicPageDonoorBrandingCard
        v-if="checkoutStep <= 1"
        :organization-name="event?.organization_name"
        :action="t('billetterie.bslugSidebar.actionReservation')"
      />
    </div>
  </div>
</template>
